import { useCallback, useEffect, useState } from 'react'
import {
  getAttendanceOverviewSummary,
  getAttendancePendingRequests,
  getTodayAttendance,
} from '../api/hrmsAttendanceLeave.api'
import { fetchDepartmentFilterOptions } from '../../HRMS-Employee-Reports/api/hrmsReportDropdowns.api'
import type {
  AttendanceRecord,
  AttendanceSummary,
  DropdownOption,
  PendingRequest,
  TabKey,
} from '../types/hrmsAttendanceLeave.types'

function getTodayLocalDate(): string {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function useHrmsAttendanceLeave() {
  const [selectedTab, setSelectedTab] = useState<TabKey>('overview')
  const [selectedDate, setSelectedDate] = useState<string>(() => getTodayLocalDate())

  // Dynamic Departments
  const [departmentOptions, setDepartmentOptions] = useState<DropdownOption[]>([])

  // 1. Aggregated Summary
  const [summary, setSummary] = useState<AttendanceSummary | null>(null)
  const [loadingSummary, setLoadingSummary] = useState<boolean>(true)

  // 2. Today's Attendance Table State
  const [todayDepartmentId, setTodayDepartmentId] = useState<string>('')
  const [todayStatus, setTodayStatus] = useState<string>('ALL')
  const [todaySearch, setTodaySearch] = useState<string>('')
  const [todayPage, setTodayPage] = useState<number>(1)
  const [todayLimit] = useState<number>(5)
  const [todayTotal, setTodayTotal] = useState<number>(0)
  const [todayRecords, setTodayRecords] = useState<AttendanceRecord[]>([])
  const [loadingToday, setLoadingToday] = useState<boolean>(false)

  // 3. Pending Requests Queue State
  const [pendingRequestType, setPendingRequestType] = useState<string>('ALL')
  const [pendingDepartmentId, setPendingDepartmentId] = useState<string>('')
  const [pendingSearch, setPendingSearch] = useState<string>('')
  const [pendingPage, setPendingPage] = useState<number>(1)
  const [pendingLimit] = useState<number>(5)
  const [pendingTotal, setPendingTotal] = useState<number>(0)
  const [pendingRequests, setPendingRequests] = useState<PendingRequest[]>([])
  const [loadingPending, setLoadingPending] = useState<boolean>(false)

  // Load department options on mount
  useEffect(() => {
    let isMounted = true
    fetchDepartmentFilterOptions().then((opts) => {
      if (isMounted) setDepartmentOptions(opts)
    })
    return () => {
      isMounted = false
    }
  }, [])

  // 1. Fetch Aggregated Summary when date changes
  useEffect(() => {
    let isMounted = true
    setLoadingSummary(true)
    getAttendanceOverviewSummary(selectedDate)
      .then((res) => {
        if (isMounted) {
          setSummary(res)
          setLoadingSummary(false)
        }
      })
      .catch((err) => {
        console.warn('Error fetching attendance overview summary:', err)
        if (isMounted) setLoadingSummary(false)
      })
    return () => {
      isMounted = false
    }
  }, [selectedDate])

  // 2. Fetch Today's Attendance quick-view table
  useEffect(() => {
    let isMounted = true
    setLoadingToday(true)
    const timeout = setTimeout(() => {
      getTodayAttendance({
        date: selectedDate,
        departmentId: todayDepartmentId,
        status: todayStatus,
        search: todaySearch,
        page: todayPage,
        limit: todayLimit,
      })
        .then((res) => {
          if (isMounted) {
            setTodayRecords(res.items)
            setTodayTotal(res.total)
            setLoadingToday(false)
          }
        })
        .catch((err) => {
          console.warn('Error fetching today attendance:', err)
          if (isMounted) setLoadingToday(false)
        })
    }, 300)

    return () => {
      isMounted = false
      clearTimeout(timeout)
    }
  }, [selectedDate, todayDepartmentId, todayStatus, todaySearch, todayPage, todayLimit])

  // 3. Fetch Unified Pending Requests queue
  useEffect(() => {
    let isMounted = true
    setLoadingPending(true)
    const timeout = setTimeout(() => {
      getAttendancePendingRequests({
        requestType: pendingRequestType,
        departmentId: pendingDepartmentId,
        search: pendingSearch,
        date: selectedDate,
        page: pendingPage,
        limit: pendingLimit,
      })
        .then((res) => {
          if (isMounted) {
            setPendingRequests(res.items)
            setPendingTotal(res.total)
            setLoadingPending(false)
          }
        })
        .catch((err) => {
          console.warn('Error fetching pending requests queue:', err)
          if (isMounted) setLoadingPending(false)
        })
    }, 300)

    return () => {
      isMounted = false
      clearTimeout(timeout)
    }
  }, [selectedDate, pendingRequestType, pendingDepartmentId, pendingSearch, pendingPage, pendingLimit])

  const handleTodayDepartmentChange = (deptId: string) => {
    setTodayDepartmentId(deptId)
    setTodayPage(1)
  }

  const handleTodayStatusChange = (status: string) => {
    setTodayStatus(status)
    setTodayPage(1)
  }

  const handleTodaySearchChange = (search: string) => {
    setTodaySearch(search)
    setTodayPage(1)
  }

  const handlePendingDepartmentChange = (deptId: string) => {
    setPendingDepartmentId(deptId)
    setPendingPage(1)
  }

  const handlePendingRequestTypeChange = (type: string) => {
    setPendingRequestType(type)
    setPendingPage(1)
  }

  const handlePendingSearchChange = (search: string) => {
    setPendingSearch(search)
    setPendingPage(1)
  }

  const removePendingRequest = useCallback((id: string) => {
    setPendingRequests((prev) => prev.filter((r) => r.id !== id))
    setPendingTotal((prev) => Math.max(0, prev - 1))
    setSummary((prev) => {
      if (!prev) return prev
      return {
        ...prev,
        pendingRequests: Math.max(0, prev.pendingRequests - 1),
      }
    })
  }, [])

  return {
    selectedTab,
    setSelectedTab,
    selectedDate,
    setSelectedDate,
    departmentOptions,

    // Summary
    summary,
    loadingSummary,

    // Today's Attendance
    todayRecords,
    todayTotal,
    todayPage,
    todayLimit,
    loadingToday,
    todaySearch,
    todayDepartmentId,
    todayStatus,
    handleTodaySearchChange,
    handleTodayDepartmentChange,
    handleTodayStatusChange,
    setTodayPage,

    // Pending Requests
    pendingRequests,
    pendingTotal,
    pendingPage,
    pendingLimit,
    loadingPending,
    pendingSearch,
    pendingDepartmentId,
    pendingRequestType,
    handlePendingSearchChange,
    handlePendingDepartmentChange,
    handlePendingRequestTypeChange,
    setPendingPage,
    removePendingRequest,

    // Backward compatibility aliases
    loading: loadingSummary && !summary,
    filteredAttendance: todayRecords,
    filteredPendingRequests: pendingRequests,
    selectedDepartment: todayDepartmentId,
    setSelectedDepartment: setTodayDepartmentId,
  }
}
